import * as React from 'react';
import { createId } from '../hooks';
import { addFilterGroup, addFilterRow, buildRowForComparatorChange, buildRowForParameterChange, buildRowWithValue, hasMissingFilterIds, normalizeFilterGroups, removeFilterRow, replaceFilterRow } from './state';
import { canAddRowForGroup, createNextRowContext } from './utils';
export function useFilterConfiguration(props) {
    const { value, onChange, parameters, maxGroups, maxRowsPerGroup, disabled = false, readOnly = false, onAddGroup, onRemoveGroup, onAddRow, onRemoveRow } = props;
    const resolveParameters = React.useCallback((context) => typeof parameters === 'function' ? parameters(context) : parameters, [parameters]);
    const groups = React.useMemo(() => normalizeFilterGroups(value, createId), [value]);
    React.useEffect(() => {
        if (hasMissingFilterIds(value)) {
            onChange(groups);
        }
    }, [groups, onChange, value]);
    const interactionDisabled = disabled || readOnly;
    const replaceRow = React.useCallback((groupIndex, rowIndex, nextRow) => {
        onChange(replaceFilterRow(groups, groupIndex, rowIndex, nextRow, createId));
    }, [groups, onChange]);
    const handleParameterChange = React.useCallback((context, descriptor) => {
        replaceRow(context.groupIndex, context.rowIndex, buildRowForParameterChange(context, descriptor));
    }, [replaceRow]);
    const handleComparatorChange = React.useCallback((context, descriptor, comparator) => {
        replaceRow(context.groupIndex, context.rowIndex, buildRowForComparatorChange(context, descriptor, comparator));
    }, [replaceRow]);
    const setRowValue = React.useCallback((context, nextValue) => {
        replaceRow(context.groupIndex, context.rowIndex, buildRowWithValue(context.row, nextValue));
    }, [replaceRow]);
    const handleAddRow = React.useCallback((groupIndex) => {
        const group = groups[groupIndex];
        const nextRowContext = createNextRowContext(group, groupIndex);
        if ((maxRowsPerGroup !== undefined &&
            group.rows.length >= maxRowsPerGroup) ||
            !canAddRowForGroup(resolveParameters(nextRowContext), nextRowContext)) {
            return;
        }
        onChange(addFilterRow(groups, groupIndex, createId));
        onAddRow?.({ group, groupIndex });
    }, [groups, maxRowsPerGroup, onAddRow, onChange, resolveParameters]);
    const handleRemoveRow = React.useCallback((context) => {
        const { group, groupIndex } = context;
        if (group.rows.length > 1) {
            onChange(removeFilterRow(groups, context));
            onRemoveRow?.(context);
            return;
        }
        if (groups.length > 1) {
            onChange(removeFilterRow(groups, context));
            onRemoveGroup?.({ group, groupIndex });
        }
    }, [groups, onChange, onRemoveGroup, onRemoveRow]);
    const handleAddGroup = React.useCallback(() => {
        if (maxGroups !== undefined && groups.length >= maxGroups) {
            return;
        }
        onChange(addFilterGroup(groups, createId));
        onAddGroup?.();
    }, [groups, maxGroups, onAddGroup, onChange]);
    const canAddGroup = !interactionDisabled &&
        (maxGroups === undefined || groups.length < maxGroups);
    return {
        groups,
        resolveParameters,
        interactionDisabled,
        canAddGroup,
        handleParameterChange,
        handleComparatorChange,
        setRowValue,
        handleAddRow,
        handleRemoveRow,
        handleAddGroup
    };
}
